import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons'

import { WidgetLayout } from '../../../lib/widget-layout'
import Text from '../../../ui/Text'
import useText from '../../../common/hooks/useText'
import { ChargePointUser } from '../../../common/redux/api/web-api'
import { SIcon } from '../../../common/features/measurement-device/measurement-devices-table-widget'
import { sortByString } from '../../../lib/sort-by-string'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { ChargersPagination } from '../../../lib/chargers-pagination'

enum SortParameter {
    FIRSTNAME = 'firstName',
}

export type UsersListWidgetProps = {
    currentPage: number
    handlePageClick: ({ selected }: unknown) => void
    fetchingData: boolean
    useractivate: (userId: string) => void
    usersData: ChargePointUser[]
    setCurrentPage: (index: number) => void
    hideButtons?: boolean
    className?: ClassName
    style?: React.CSSProperties
}

export const UsersListWidget = React.memo(function UsersListWidget({
    className,
    useractivate,
    setCurrentPage,
    fetchingData,
    usersData,
    currentPage,
    style,
}: UsersListWidgetProps): React.ReactElement {
    const text = useText()
    const [pageSize] = useState(50)

    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; reversed: boolean }>({
        parameter: SortParameter.FIRSTNAME,
        reversed: false,
    })
    const offset = currentPage * pageSize

    return (
        <WidgetLayout className={cn(className)} style={style} title={text('lblUsersWidget')} data-testid="users">
            {!fetchingData && (
                <div className="heading5 margin-bottom-10" data-testid="noOfUsers">
                    <Text code="lblUsersFound" values={{ users: usersData?.length }}></Text>
                </div>
            )}
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell data-testid="table-th-name">
                            <a
                                data-testid="table-th-name-link"
                                onClick={() =>
                                    setSortFilter({
                                        parameter: SortParameter.FIRSTNAME,
                                        reversed: !sortFilter?.reversed,
                                    })
                                }
                                className="pointer"
                            >
                                <Text code="lblName"></Text>
                                {sortFilter?.parameter === SortParameter.FIRSTNAME && (
                                    <SIcon data-testid="icon-sn" icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                )}
                            </a>
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-email">{text('lblEmail')}</TableHeaderCell>

                        <TableHeaderCell data-testid="table-th-status">{text('lblStatus')}</TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {usersData
                        ?.slice(offset, offset + pageSize)
                        .sort((a, b) =>
                            sortByString(
                                (a[sortFilter.parameter] as string) ?? '',
                                (b[sortFilter.parameter] as string) ?? '',
                                sortFilter.reversed
                            )
                        )
                        .map((user) => (
                            <TableRow key={user?.id}>
                                <TableBodyCell data-testid={`table-tb-name-${user.firstName}`}>
                                    {user.firstName} {user.lastName}
                                </TableBodyCell>
                                <TableBodyCell data-testid={`table-tb-name-${user.email}`}>{user.email}</TableBodyCell>

                                <TableBodyCell data-testid={`table-tb-name-${user.id}`}>
                                    {user.userStatus == 'New' ? (
                                        <span data-testid="table-tb-activateUser">{text('lblNewUserPage')}</span>
                                    ) : (
                                        <div>{text('lblActivated')}</div>
                                    )}

                                    {user.userStatus == 'New' && (
                                        <button
                                            data-testid="table-btn-activateUser"
                                            type="submit"
                                            className="btn btn-ok btn-sm  margin-left-med"
                                            onClick={() => useractivate(user?.id)}
                                        >
                                            {text('btnActivate')}
                                        </button>
                                    )}
                                </TableBodyCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            {usersData?.length > 100 && (
                <ChargersPagination
                    pageIndex={currentPage}
                    setPageIndex={(pageIndex: number) => setCurrentPage(pageIndex)}
                    totalCount={usersData.length}
                    pageSize={pageSize}
                    displayedChargers={pageSize}
                />
            )}
            {fetchingData && <div className="shimmer-container shimmer-large"></div>}
        </WidgetLayout>
    )
})
